<script>
import {ref, toRef, onMounted, onBeforeUnmount} from "vue";
import {router} from "@inertiajs/vue3";

import {useSearchStore} from "@js/store/SearchStore.js";
import {useSwipePanel} from "@js/useSwipePanel.js";

import IconX from "@js/components/Icons/IconX.vue";
import OnlyMobile from "@js/components/DynamicComponents/OnlyMobile.vue";
import ImageSearchUploadSection from "@js/components/ImageSearch/ImageSearchUploadSection.vue";

export default {
    name: "ImageSearchPopup",
    components: {
        ImageSearchUploadSection,
        OnlyMobile,
        IconX,
    },
    setup() {
        const searchStore = useSearchStore();

        const modalRef = ref(null);
        const dragHandleRef = ref(null);
        const isMobilePanel = ref(false);

        const updateMobileState = () => {
            isMobilePanel.value = window.innerWidth < 1024;
        };

        const {
            rendered,
            panelStyle,
            backdropStyle,
            close,
            handleBackgroundClick,
        } = useSwipePanel({
            isOpen: toRef(searchStore.imageSearch, "isOpen"),
            panelRef: modalRef,
            handleRef: dragHandleRef,
            enabled: isMobilePanel,
            direction: "bottom",
            closeThreshold: 110,
            onClose: () => {
                searchStore.hideImageSearch();
            },
        });

        let removeRouterListener = null;

        onMounted(() => {
            updateMobileState();
            window.addEventListener("resize", updateMobileState);

            removeRouterListener = router.on("before", () => {
                searchStore.hideImageSearch();
            });
        });

        onBeforeUnmount(() => {
            window.removeEventListener("resize", updateMobileState);

            if (typeof removeRouterListener === "function") {
                removeRouterListener();
            }
        });

        return {
            searchStore,
            modalRef,
            dragHandleRef,
            isMobilePanel,
            rendered,
            panelStyle,
            backdropStyle,
            closeModal: close,
            handleBackgroundClick,
        };
    },
};
</script>

<template>
    <div
        v-if="rendered"
        class="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
        @click="handleBackgroundClick"
    >
        <div
            class="absolute inset-0 bg-black/70"
            :style="backdropStyle"
        ></div>

        <div
            ref="modalRef"
            :style="isMobilePanel ? panelStyle : {}"
            class="relative z-[1] bg rounded-tr-2xl rounded-tl-2xl sm:rounded-2xl shadow-lg max-h-[450px] sm:max-w-[800px] sm:max-h-[450px] w-full h-full overflow-hidden"
            @click.stop
        >
            <button
                @click.prevent="closeModal"
                class="absolute z-20 text-gray-400 focus:outline-none transition
                       top-2 right-2
                       md:-top-10 md:-right-10 md:hover:text-white"
            >
                <IconX
                    class="h-7 w-7 md:h-10 md:w-10 stroke-[#000] md:stroke-[#fff] active:stroke-baseColor md:hover:stroke-baseColor transition"
                />
            </button>

            <OnlyMobile :maxSize="1024">
                <div class="absolute top-3 right-0 left-0 m-auto z-10 flex justify-center">
                    <div
                        ref="dragHandleRef"
                        class="h-8 w-24 flex items-start justify-center select-none"
                        style="touch-action: none; user-select: none;"
                    >
                        <div class="h-[5px] w-[50px] bg-gray-400 rounded-full"></div>
                    </div>
                </div>
            </OnlyMobile>

            <div class="w-full h-full p-5 md:backdrop-blur-md">
                <ImageSearchUploadSection/>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.bg {
    background-color: white;
}

@media (min-width: 1024px) {
    .bg {
        background: linear-gradient(rgba(0, 0, 0, 0.65), rgba(0, 0, 0, 0.65));
    }
}
</style>
